import React, { useState, useEffect } from 'react';
import {
  Container,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  TextField,
  TablePagination,
  TableSortLabel,
  Box,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useUserContext } from '../../../context_providers/user_context/UserProvider';
import Loading from '../../../components/loading/Loading';
import PageHeader from '../../../components/utils/PageHeader';
import COLORS from '../../../resources/style/Themes';
import { CSVDownload } from 'react-csv';
import { getAllErrorLogs } from '../../../repository/CompassErrorLogDAO';
import { getDFUFileNames } from '../../../repository/DFUDAO';
import { Add } from '@mui/icons-material';
import {
  ToastType,
  useToast,
} from '../../../context_providers/toast_context/ToastContext';

const columns = [
  { id: 'version', label: 'Version', sortable: false },
  { id: 'uuid', label: 'UUID', sortable: true },
  { id: 'error', label: 'Error', sortable: true },
  { id: 'line', label: 'Line', numeric: true, sortable: true },
  { id: 'module', label: 'Module', numeric: true, sortable: true },
  { id: 'type', label: 'Type', numeric: true, sortable: true },
  { id: 'date', label: 'Date', sortable: true },
  { id: 'orgCode', label: 'Org Code', sortable: true },
  { id: 'authId', label: 'Auth ID', sortable: true },
];

export const CompassErrorLog = () => {
  const [responseDataVersions, setResponseDataVersions] = useState([]);
  const [responseDataErrors, setResponseDataErrors] = useState('');
  const { bearerToken } = useUserContext();
  const [selectedVersion, setSelectedVersion] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [rows, setRows] = useState([]);
  const [downloadCSV, setDownloadCSV] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const open = Boolean(anchorEl);

  const descendingComparator = (a, b, orderBy) => {
    const aValue = a[columns.findIndex((col) => col.id === orderBy)];
    const bValue = b[columns.findIndex((col) => col.id === orderBy)];

    if (columns.find((col) => col.id === orderBy)?.numeric) {
      return Number(bValue) - Number(aValue);
    }

    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    const fetchDFUFileNames = async () => {
      try {
        const response = await getDFUFileNames();
        if (response) {
          const versions = Array.isArray(response)
            ? response
            : Object.keys(response);
          setResponseDataVersions(versions);
        } else {
          showToast(
            'Failed to fetch DFU file names. Please try again.',
            ToastType.DANGER,
          );
        }
      } catch (error) {
        showToast(
          'An error occurred while fetching firmware versions.',
          ToastType.DANGER,
        );
      }
    };
    fetchDFUFileNames();
  }, [bearerToken]);

  const fillTable = (csvData) => {
    if (!csvData) return;
    const tempRows = csvData.split('\n');
    const innerRows = tempRows.slice(1).map((row) => row.split(','));
    setRows(innerRows);
    setDownloadCSV(false);
    setPage(0); // Reset to first page when new data is loaded
  };

  const cleanCompassVersion = (compassVersion) => {
    return compassVersion.replace('.zip', '').replace('CompassProDFU_', '');
  };

  const fetchErrorLogs = async (version) => {
    setIsLoading(true);
    try {
      const response = await getAllErrorLogs(version);

      const result = await response.text();
      setResponseDataErrors(result);
      fillTable(result);
    } catch (error) {
      showToast(
        'An error occurred while fetching error logs.',
        ToastType.DANGER,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (version) => {
    setAnchorEl(null);
    if (version) {
      setSelectedVersion(version);
      const cleanedVersion = cleanCompassVersion(version);
      fetchErrorLogs(cleanedVersion);
    }
  };

  const handleDownload = () => {
    setDownloadCSV(true);
  };

  // Filter function for search
  const filterRows = (rows) => {
    if (!searchQuery) return rows;

    return rows.filter((row) =>
      row.some((cell) =>
        cell.toString().toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    );
  };

  const getProcessedRows = () => {
    const filteredRows = filterRows(rows);
    return filteredRows.sort(getComparator(order, orderBy));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value === -1 ? getProcessedRows().length : value);
    setPage(0);
  };

  const getPaginatedRows = (rows) => {
    if (rowsPerPage === -1) return rows;
    return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const renderData = () => (
    <>
      <PageHeader
        action={handleDownload}
        icon={<Add width={14} height={14} />}
        header={'Compass Error Logs'}
        label={'Download .csv File'}
        disabled={!responseDataErrors || downloadCSV}
      />
      {responseDataErrors && downloadCSV && (
        <CSVDownload data={responseDataErrors} enclosingCharacter={`,`} />
      )}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleMenuClick}
          style={{
            height: '42px',
          }}
        >
          {selectedVersion || 'Select Firmware Version'}
        </Button>
        <TextField
          label="Search"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          style={{ minWidth: '200px' }}
          disabled={!selectedVersion || isLoading}
          placeholder={
            !selectedVersion
              ? 'Select firmware version first'
              : 'Search in table...'
          }
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleMenuClose(null)}
      >
        {responseDataVersions.map((version, index) => (
          <MenuItem key={index} onClick={() => handleMenuClose(version)}>
            {version}
          </MenuItem>
        ))}
      </Menu>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={
                        column.sortable && orderBy === column.id ? order : false
                      }
                      style={{ fontWeight: 'bold' }}
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => handleRequestSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {getPaginatedRows(getProcessedRows()).map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={cellIndex}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
                {getProcessedRows().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="h6" align="center">
                        {selectedVersion
                          ? 'No Errors Found.'
                          : 'Please select a firmware version'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, { label: 'All', value: -1 }]}
            component="div"
            count={getProcessedRows().length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </>
  );

  return (
    <Container maxWidth="xl" style={{ paddingBottom: '100px' }}>
      {responseDataVersions ? renderData() : <CircularProgress />}
    </Container>
  );
};

export default withAuthenticationRequired(CompassErrorLog, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
